/**
 * dictation-store.ts
 * Where dictations recorded with no signal wait on the phone
 * (operator-dictation-queue): one IndexedDB object store keyed by the
 * dictation's clientIntakeId, holding the audio Blob itself. IndexedDB, not
 * localStorage, because only IndexedDB stores Blobs and survives the PWA
 * being closed by the OS. Plain IndexedDB with no wrapper dependency: the
 * three operations below are all the queue needs.
 *
 * `DictationStore` is the seam the queue talks to, so its tests run against
 * an in-memory store (jsdom has no IndexedDB).
 */

export type DictationState = 'waiting' | 'failed' | 'ready';

export interface QueuedDictation {
  readonly clientIntakeId: string;
  /** `${tenantId}:${sub}` of whoever recorded it; a shared phone never mixes users. */
  readonly owner: string;
  /** When it was recorded (ISO), shown so the worker recognizes it. */
  readonly recordedAt: string;
  readonly durationMs: number;
  readonly assetId?: string;
  readonly state: DictationState;
  /** Dropped once the server has it. */
  readonly audio?: Blob;
  /** Set once sent: the intake to confirm. */
  readonly voiceIntakeId?: string;
  /** Why the server rejected it (state 'failed'). */
  readonly error?: string;
}

export interface DictationStore {
  all(): Promise<QueuedDictation[]>;
  put(row: QueuedDictation): Promise<void>;
  remove(clientIntakeId: string): Promise<void>;
}

const DB_NAME = 'rigx-offline';
const DB_VERSION = 1;
const STORE = 'dictations';

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'clientIntakeId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return promisify(run(db.transaction(STORE, mode).objectStore(STORE)));
}

export const indexedDbDictationStore: DictationStore = {
  all: () => withStore('readonly', (s) => s.getAll() as IDBRequest<QueuedDictation[]>),
  put: async (row) => {
    await withStore('readwrite', (s) => s.put(row));
  },
  remove: async (id) => {
    await withStore('readwrite', (s) => s.delete(id));
  },
};
